import { usePathname } from 'next/navigation';
import { ChangeEvent, FC, useMemo, useState } from 'react';
import { useThrottle } from 'react-use';

import { CTAButton } from '@/general/components/Button/CTAButton';
import { ControlToggleButtonInputGroup } from '@/general/components/forms/Input/ControlInput/ControlToggleButtonInputGroup';
import { Input } from '@/general/components/forms/Input/Input';
import { formatNumber } from '@/general/helpers/formatNumber';

import {
  CALCULATOR_MAX_DEPOSIT,
  CALCULATOR_MAX_PURCHASE,
  CALCULATOR_MIN_DEPOSIT,
  CALCULATOR_MIN_PURCHASE,
  DEFAULT_DEPOSIT_AMOUNT,
  DEFAULT_INTEREST_RATE,
  DEFAULT_TERM_OF_LOAN,
  THROTTLE_TIME,
  calculateWeeklyPayment,
} from '../../FinanceCalculator.helper';
import { FinanceCalculatorSlider } from '../../FinanceCalculatorSlider';

type Props = {
  loanTermsArray: number[];
  interestRateRange: {
    minRate: number;
    maxRate: number;
  };
  productPrice: number;
};

export const MyRepayment: FC<Props> = ({ loanTermsArray, interestRateRange, productPrice }) => {
  const pathName = usePathname();
  const [purchasePrice, setPurchasePrice] = useState(productPrice);
  const [depositAmount, setDepositAmount] = useState(DEFAULT_DEPOSIT_AMOUNT);
  const [interestRate, setInterestRate] = useState(DEFAULT_INTEREST_RATE);
  const [termOfLoan, setTermOfLoan] = useState(DEFAULT_TERM_OF_LOAN);

  const throttledPurchasePrice = useThrottle(purchasePrice, THROTTLE_TIME);
  const throttledDepositAmount = useThrottle(depositAmount, THROTTLE_TIME);
  const throttledInterestRate = useThrottle(interestRate, THROTTLE_TIME);
  const throttledTermOfLoan = useThrottle(termOfLoan, THROTTLE_TIME);

  const weeklyPayment = useMemo(() => {
    return calculateWeeklyPayment(
      throttledPurchasePrice,
      throttledInterestRate,
      throttledTermOfLoan,
      throttledDepositAmount,
    );
  }, [throttledPurchasePrice, throttledInterestRate, throttledTermOfLoan, throttledDepositAmount]);

  const onChangePurchasePrice = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPurchasePrice(Number(value));
  };
  const onChangeDepositAmount = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDepositAmount(Number(value));
  };
  const onChangeInterestRate = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInterestRate(Number(value));
  };

  return (
    <form className="grid gap-y-8 sm:gap-y-6 md:gap-y-8">
      <div>
        <div className="mb-2 text-lg font-extrabold text-tex-def-tie1">How much do you want to spend?</div>
        <div className="grid gap-x-4 sm:grid-cols-2 md:gap-x-6">
          <Input
            id="purchasePrice"
            name="purchasePrice"
            value={`${purchasePrice}`}
            onChange={onChangePurchasePrice}
            iconLeft="$"
            label={'Purchase price'}
            type="number"
            min={CALCULATOR_MIN_PURCHASE}
            max={CALCULATOR_MAX_PURCHASE}
            data-testid="finance-calculator-purchase-price"
          />
          <Input
            id="depositAmount"
            name="depositAmount"
            value={`${depositAmount}`}
            onChange={onChangeDepositAmount}
            iconLeft="$"
            label={'Deposit amount'}
            type="number"
            min={CALCULATOR_MIN_DEPOSIT}
            max={CALCULATOR_MAX_DEPOSIT}
            data-testid="finance-calculator-deposit-amount"
          />
        </div>
      </div>
      <div className="grid gap-y-2">
        <div className="text-lg font-extrabold text-tex-def-tie1">Term of loan (years)</div>
        <ControlToggleButtonInputGroup
          id="termOfLoan"
          name="termOfLoan"
          value={`${termOfLoan}`}
          onChange={(value) => setTermOfLoan(Number(value))}
          data-testid="finance-calculator-loan-term"
          options={loanTermsArray.map((item) => ({
            value: `${item}`,
            label: `${item}`,
          }))}
        />
      </div>
      <FinanceCalculatorSlider
        minRate={interestRateRange.minRate}
        maxRate={interestRateRange.maxRate}
        value={`${interestRate}`}
        onChange={onChangeInterestRate}
        data-testid="finance-calculator-interest-rate"
      />
      <div className="grid gap-y-4 rounded-lg border-0 bg-bac-tie2 p-4 text-center sm:grid-cols-2 sm:items-center md:grid-cols-none md:gap-x-6">
        <div>
          <div className="mb-2 text-tex-def-tie2">Your estimated weekly repayment</div>
          <span className="c-fs-h2 font-extrabold text-tex-def-tie1" data-testid="finance-calculator-estimated-fee">
            {formatNumber(weeklyPayment ?? 0, { prepend: '$' })}
          </span>
          <span className="c-fs-h4 font-extrabold text-tex-def-tie1">/week*</span>
        </div>

        <CTAButton
          size="medium"
          variant="accent"
          data-testid="start-finance-journey-btn"
          {...{
            label: 'Get personalised quotes',
            buttonType: 'link',
            href: `/forms/-/personalised-quote?purchasePrice=${purchasePrice}&depositAmount=${depositAmount}&origin=${pathName}`,
          }}
        />
      </div>
    </form>
  );
};
